<template>
  <div class="donora-page">
    <main class="donora-content">
      <section class="donora-hero-card">
        <div class="donora-hero-text">
          <div class="donora-logo" aria-label="Donora">
            <span>DON</span>
            <svg class="logo-heart" viewBox="0 0 44 40" aria-hidden="true">
              <defs>
                <clipPath id="donoraLogoHeartClip">
                  <path
                    d="M22 36S4 25.8 4 13.7C4 8.4 8.1 4.4 13.1 4.4c3.5 0 6.8 2 8.9 5.2 2.1-3.2 5.4-5.2 8.9-5.2 5 0 9.1 4 9.1 9.3C40 25.8 22 36 22 36Z"
                  />
                </clipPath>
              </defs>
              <g clip-path="url(#donoraLogoHeartClip)">
                <rect width="44" height="20" fill="#126cff" />
                <rect y="20" width="44" height="20" fill="#ffd21a" />
              </g>
              <path
                d="M22 36S4 25.8 4 13.7C4 8.4 8.1 4.4 13.1 4.4c3.5 0 6.8 2 8.9 5.2 2.1-3.2 5.4-5.2 8.9-5.2 5 0 9.1 4 9.1 9.3C40 25.8 22 36 22 36Z"
                fill="none"
                stroke="#fff"
                stroke-width="3"
                stroke-linejoin="round"
              />
            </svg>
            <span>RA</span>
            <small>{{ $t("donora.logoSubtitle") }}</small>
          </div>
          <h1>
            {{ $t("donora.heroTitleTop") }}
            <span>{{ $t("donora.heroTitleHighlight") }}</span>
          </h1>
          <h2>{{ $t("donora.heroSubtitle") }}</h2>
          <p>
            {{ $t("donora.heroDescription") }}
          </p>
        </div>

        <div class="donora-hero-art">
          <img
            :src="heroImage"
            alt=""
            aria-hidden="true"
            decoding="async"
            fetchpriority="high"
            loading="eager"
          />
        </div>

        <div
          class="donora-categories"
          :aria-label="$t('donora.categoriesAria')"
        >
          <div class="category-card green">
            <svg viewBox="0 0 64 64" aria-hidden="true">
              <path d="M10 34c0-12 10-22 22-22s22 10 22 22" />
              <path d="M12 36h12l8 6 8-6h12" />
              <path d="M14 36c0 7 5 12 12 12h2" />
              <path d="M50 36c0 7-5 12-12 12h-2" />
            </svg>
            <span>{{ $t("donora.categories.defenders") }}</span>
          </div>
          <div class="category-card blue">
            <svg viewBox="0 0 64 64" aria-hidden="true">
              <circle cx="24" cy="21" r="9" />
              <path d="M9 52c0-10 7-17 15-17s15 7 15 17" />
              <path d="M43 20c6 1 10 6 10 12s-4 11-10 12" />
            </svg>
            <span>{{ $t("donora.categories.refugees") }}</span>
          </div>
          <div class="category-card yellow">
            <svg viewBox="0 0 64 64" aria-hidden="true">
              <path
                d="M32 12c-8 0-14 6-14 14v6c0 8 6 14 14 14s14-6 14-14v-6c0-8-6-14-14-14Z"
              />
              <path d="M20 26c8 0 13-3 17-9 2 5 5 8 9 9" />
              <path d="M24 52c4 3 12 3 16 0" />
              <path d="M26 31h.1M38 31h.1M27 38c3 3 7 3 10 0" />
            </svg>
            <span>{{ $t("donora.categories.children") }}</span>
          </div>
          <div class="category-card purple">
            <svg viewBox="0 0 64 64" aria-hidden="true">
              <circle cx="21" cy="21" r="6" />
              <circle cx="43" cy="21" r="6" />
              <circle cx="14" cy="35" r="6" />
              <circle cx="50" cy="35" r="6" />
              <path
                d="M20 52c3-13 21-13 24 0 1 4-3 6-7 4-3-1-7-1-10 0-4 2-8 0-7-4Z"
              />
            </svg>
            <span>{{ $t("donora.categories.animals") }}</span>
          </div>
          <div class="category-card white">
            <svg viewBox="0 0 64 64" aria-hidden="true">
              <path d="M12 22 32 12l20 10v24L32 56 12 46Z" />
              <path d="M12 22 32 32l20-10M32 32v24" />
              <path d="m42 39 4 4 8-9" />
            </svg>
            <span>{{ $t("donora.categories.humanitarian") }}</span>
          </div>
        </div>
      </section>

      <button class="donora-primary" type="button" @click="openDonora">
        <span class="button-content">
          <span class="button-heart">
            <svg viewBox="0 0 64 64" aria-hidden="true">
              <path
                d="M32 52S10 39 10 23c0-7 5-12 12-12 4 0 8 2 10 6 2-4 6-6 10-6 7 0 12 5 12 12 0 16-22 29-22 29Z"
              />
            </svg>
          </span>
          <span>
            <strong>{{ $t("donora.ctaTitle") }}</strong>
            <small>{{ $t("donora.ctaSubtitle") }}</small>
          </span>
        </span>
        <span class="button-arrow">›</span>
      </button>

      <section class="donora-actions">
        <h2>{{ $t("donora.actionsTitle") }} <span>Donora</span></h2>

        <div class="action-row blue">
          <div class="action-icon">
            <svg viewBox="0 0 64 64" aria-hidden="true">
              <path
                d="M32 52S10 39 10 23c0-7 5-12 12-12 4 0 8 2 10 6 2-4 6-6 10-6 7 0 12 5 12 12 0 16-22 29-22 29Z"
              />
            </svg>
          </div>
          <div>
            <h3>{{ $t("donora.actions.helpTitle") }}</h3>
            <p>
              {{ $t("donora.actions.helpText") }}
            </p>
          </div>
        </div>

        <div class="action-row yellow">
          <div class="action-icon">
            <svg viewBox="0 0 64 64" aria-hidden="true">
              <rect x="14" y="14" width="36" height="36" rx="9" />
              <path d="M32 23v18M23 32h18" />
            </svg>
          </div>
          <div>
            <h3>{{ $t("donora.actions.createTitle") }}</h3>
            <p>
              {{ $t("donora.actions.createText") }}
            </p>
          </div>
        </div>

        <div class="action-row green">
          <div class="action-icon">
            <svg viewBox="0 0 64 64" aria-hidden="true">
              <path d="M32 8 52 18v14c0 13-8 21-20 26-12-5-20-13-20-26V18Z" />
              <path d="m22 32 7 7 14-16" />
            </svg>
          </div>
          <div>
            <h3>{{ $t("donora.actions.verifiedTitle") }}</h3>
            <p>{{ $t("donora.actions.verifiedText") }}</p>
          </div>
        </div>

        <div class="action-row purple">
          <div class="action-icon">
            <svg viewBox="0 0 64 64" aria-hidden="true">
              <circle cx="17" cy="32" r="6" />
              <circle cx="47" cy="16" r="6" />
              <circle cx="47" cy="48" r="6" />
              <path d="m23 29 18-10M23 35l18 10" />
            </svg>
          </div>
          <div>
            <h3>{{ $t("donora.actions.shareTitle") }}</h3>
            <p>
              {{ $t("donora.actions.shareText") }}
            </p>
          </div>
        </div>

        <div class="action-row blue">
          <div class="action-icon">
            <svg viewBox="0 0 64 64" aria-hidden="true">
              <path d="M13 47V35M32 47V17M51 47V26" />
              <path d="M13 47h.1M32 47h.1M51 47h.1" />
            </svg>
          </div>
          <div>
            <h3>{{ $t("donora.actions.resultTitle") }}</h3>
            <p>
              {{ $t("donora.actions.resultText") }}
            </p>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import Donora from "@/plugins/api/factories/Donora";
import heroImage from "@/assets/images/donora-hero.jpg";

const DONORA_LINK =
  "https://donora.help/app?utm_source=zakarpattia_energo&utm_medium=partner&utm_campaign=ua_utilities_app_install&utm_content=energy";

export default {
  name: "Donora",
  data() {
    return {
      heroImage,
    };
  },
  methods: {
    async openDonora() {
      const link = this.getDonoraLink();

      try {
        await Donora.link(this.getDonoraLinkPayload(link));
      } catch (error) {
        console.error("Donora link tracking error:", error);
      } finally {
        this.openExternalUrl(link);
      }
    },
    getDonoraLinkPayload(link) {
      return {
        account_number: this.getAccountNumber(),
        address: this.getAddress(),
        link,
      };
    },
    getAccountNumber() {
      if (this.$route.params.account) {
        return this.$route.params.account;
      }

      if (this.$route.query.account) {
        return this.$route.query.account;
      }

      const auth = this.$store.state.Auth;

      if (auth.account) {
        return auth.account.accountNumber || auth.account.ls || null;
      }

      if (auth.accountNumber) {
        return auth.accountNumber;
      }

      const accountKeys = Object.keys(auth.accounts || {});
      if (accountKeys.length) {
        return accountKeys[0];
      }

      return null;
    },
    getCurrentAccount() {
      const accountNumber = this.getAccountNumber();
      const auth = this.$store.state.Auth;

      if (accountNumber && auth.accounts && auth.accounts[accountNumber]) {
        return auth.accounts[accountNumber];
      }

      if (auth.account) {
        const currentNumber = auth.account.accountNumber || auth.account.ls;
        if (!accountNumber || String(currentNumber) === String(accountNumber)) {
          return auth.account;
        }
      }

      return null;
    },
    getAddress() {
      if (this.$route.query.address) {
        return this.$route.query.address;
      }

      return this.getAddressFromAccount(this.getCurrentAccount());
    },
    getAddressFromAccount(account) {
      if (!account) {
        return null;
      }

      if (account.address) {
        return account.address;
      }

      const apartment = account.appartment || account.apartment;
      const street = [
        account.streetType || account.street_type,
        account.streetName || account.street_name || account.street,
        account.building ||
          account.house ||
          account.buildingNumber ||
          account.building_number,
        apartment ? "\u043A\u0432. " + apartment : null,
      ]
        .filter((part) => part)
        .join(" ");
      const city = account.city || account.cityName || account.city_name;

      return [street, city].filter((part) => part).join(", ") || null;
    },
    getDonoraLink() {
      return DONORA_LINK;
    },
    openExternalUrl(url) {
      if (typeof this.nativeSendEvent === "function") {
        this.nativeSendEvent("url", url);
        return;
      }

      window.open(url, "_blank", "noopener");
    },
  },
};
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.donora-page {
  min-height: 100vh;
  background: radial-gradient(
      circle at 78% 12%,
      rgba(0, 112, 255, 0.14),
      transparent 32%
    ),
    radial-gradient(circle at 8% 22%, rgba(255, 204, 0, 0.08), transparent 28%),
    #050a12;
  color: #fff;
  padding-bottom: 16px;
}

.donora-content {
  width: calc(100% - 20px);
  max-width: 414px;
  margin: 0 auto;
  padding: 10px 0 14px;
}

.donora-hero-card,
.donora-actions {
  overflow: hidden;
  border: 1px solid rgba(46, 74, 112, 0.72);
  border-radius: 16px;
  background: linear-gradient(
      145deg,
      rgba(9, 18, 31, 0.96),
      rgba(4, 10, 18, 0.98)
    ),
    #07111f;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
}

.donora-hero-card {
  position: relative;
  padding: 16px 15px 12px;
  background: #000208;
}

.donora-hero-card::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background: linear-gradient(
      90deg,
      rgba(0, 2, 8, 0.9) 0%,
      rgba(0, 2, 8, 0.72) 36%,
      rgba(0, 2, 8, 0.1) 60%,
      rgba(0, 2, 8, 0) 100%
    ),
    linear-gradient(
      180deg,
      rgba(0, 2, 8, 0) 0%,
      rgba(0, 2, 8, 0.03) 58%,
      rgba(0, 2, 8, 0.42) 100%
    );
}

.donora-hero-text,
.donora-categories {
  position: relative;
  z-index: 2;
}

.donora-logo {
  display: inline-flex;
  align-items: center;
  position: relative;
  margin-bottom: 16px;
  color: #fff;
  font-size: clamp(22px, 6vw, 25px);
  font-weight: 900;
  line-height: 1;
  letter-spacing: 3px;
  text-shadow: 0 1px 8px rgba(255, 255, 255, 0.16);
}

.donora-logo small {
  position: absolute;
  left: 0;
  top: 27px;
  color: #aeb8ca;
  font-size: clamp(7px, 2vw, 8px);
  font-weight: 600;
  letter-spacing: 1.8px;
  white-space: nowrap;
}

.logo-heart {
  width: 23px;
  height: 21px;
  display: block;
  margin: 0 3px 1px 1px;
  overflow: visible;
  flex: 0 0 auto;
}

.donora-hero-text h1 {
  margin: 0;
  color: #fff;
  font-size: clamp(24px, 7.1vw, 31px);
  font-weight: 850;
  line-height: 1.02;
  letter-spacing: 0;
}

.donora-hero-text h1 span {
  display: block;
  color: #ffd21a;
}

.donora-hero-text h2 {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0 12px;
  color: #fff;
  font-size: clamp(14px, 4vw, 18px);
  font-weight: 800;
  line-height: 1.16;
}

.donora-hero-text p {
  max-width: 206px;
  margin: 0 0 11px;
  color: #c4ccda;
  font-size: clamp(11.5px, 3.2vw, 12.5px);
  line-height: 1.38;
}

.donora-hero-art {
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
}

.donora-hero-art::before {
  content: "";
  position: absolute;
  right: -72px;
  top: -22px;
  width: 286px;
  height: 286px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(25, 120, 255, 0.24) 0%,
    rgba(255, 210, 26, 0.12) 36%,
    rgba(0, 2, 8, 0) 70%
  );
}

.donora-hero-art img {
  position: absolute;
  right: -94px;
  top: -34px;
  width: 342px;
  max-width: none;
  display: block;
  opacity: 1;
}

.donora-categories {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 6px;
  margin-top: 13px;
}

.category-card {
  min-width: 0;
  min-height: 58px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  padding: 7px 3px 6px;
  border: 1px solid rgba(46, 74, 112, 0.48);
  border-radius: 12px;
  background: rgba(14, 24, 38, 0.78);
  color: #fff;
  text-align: center;
}

.category-card svg {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  fill: none;
  stroke: currentColor;
  stroke-width: 3.2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.category-card.green svg {
  stroke: #5ee56b !important;
}

.category-card.blue svg {
  stroke: #1978ff !important;
}

.category-card.yellow svg {
  stroke: #ffd21a !important;
}

.category-card.purple svg {
  stroke: #8d5cff !important;
}

.category-card.white svg {
  stroke: #f5f7fb !important;
}

.category-card span {
  color: #fff;
  font-size: clamp(8px, 2.35vw, 9px);
  font-weight: 650;
  line-height: 1.18;
}

.category-card.green,
.action-row.green .action-icon {
  color: #5ee56b;
}

.category-card.blue,
.action-row.blue .action-icon {
  color: #1978ff;
}

.category-card.yellow,
.action-row.yellow .action-icon {
  color: #ffd21a;
}

.category-card.purple,
.action-row.purple .action-icon {
  color: #8d5cff;
}

.category-card.white {
  color: #f5f7fb;
}

.donora-actions {
  margin-top: 0;
  padding: 13px 15px 10px;
}

.donora-actions h2 {
  margin: 0 0 10px;
  color: #fff;
  font-size: clamp(16px, 4.6vw, 18px);
  font-weight: 850;
  line-height: 1.14;
  letter-spacing: 0;
}

.donora-actions h2 span {
  color: #1778ff;
}

.action-row {
  display: grid;
  grid-template-columns: 39px minmax(0, 1fr);
  gap: 10px;
  padding: 0 0 9px;
  margin-bottom: 9px;
  border-bottom: 1px solid rgba(45, 67, 98, 0.62);
}

.action-row:last-child {
  border-bottom: 0;
  margin-bottom: 0;
}

.action-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(35, 117, 255, 0.5);
  border-radius: 50%;
  background: rgba(17, 32, 53, 0.62);
}

.action-row svg {
  width: 21px;
  height: 21px;
  fill: none;
  stroke: currentColor;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.action-row.green svg {
  stroke: #5ee56b !important;
}

.action-row.blue svg {
  stroke: #1978ff !important;
}

.action-row.yellow svg {
  stroke: #ffd21a !important;
}

.action-row.purple svg {
  stroke: #8d5cff !important;
}

.action-row h3 {
  margin: 1px 0 4px;
  color: #fff;
  font-size: clamp(13px, 3.6vw, 14px);
  font-weight: 800;
  line-height: 1.22;
}

.action-row p {
  margin: 0;
  color: #aeb8ca;
  font-size: clamp(11.5px, 3.2vw, 12.5px);
  line-height: 1.32;
}

.donora-primary {
  box-sizing: border-box;
  position: relative;
  bottom: auto;
  z-index: 5;
  width: 100%;
  min-height: 58px;
  max-width: none;
  display: grid;
  grid-template-columns: 1fr 18px;
  align-items: center;
  gap: 8px;
  margin: 8px auto 10px;
  padding: 8px 11px;
  border: 1px solid rgba(85, 143, 255, 0.82);
  border-radius: 13px;
  background: linear-gradient(135deg, #176cff 0%, #124ad9 100%);
  box-shadow: 0 14px 30px rgba(23, 108, 255, 0.34);
  color: #fff;
  text-align: center;
}

.button-content {
  min-width: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding-left: 12px;
}

.button-heart {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  transform-origin: center;
  animation: donora-heart-pulse 1.35s ease-in-out infinite;
}

.button-heart svg {
  width: 40px;
  height: 40px;
  fill: none;
  stroke: currentColor;
  stroke-width: 4.2;
  stroke-linecap: round;
  stroke-linejoin: round;
  filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.38));
}

@keyframes donora-heart-pulse {
  0%,
  100% {
    transform: scale(1);
  }

  45% {
    transform: scale(1.12);
  }
}

.donora-primary strong,
.donora-primary small {
  display: block;
}

.donora-primary strong {
  font-size: clamp(15px, 4.25vw, 16.5px);
  font-weight: 900;
  line-height: 1.08;
  letter-spacing: 0;
}

.donora-primary small {
  margin-top: 3px;
  color: rgba(255, 255, 255, 0.88);
  font-size: clamp(11.2px, 3.25vw, 12.4px);
  font-weight: 650;
  line-height: 1.14;
}

.button-arrow {
  justify-self: end;
  font-size: 22px;
  font-weight: 250;
  line-height: 1;
}

@media (max-width: 430px) {
  .donora-content {
    width: calc(100% - 18px);
  }
}

@media (max-width: 390px) {
  .donora-content {
    width: calc(100% - 16px);
  }

  .donora-hero-card {
    padding: 15px 13px 11px;
  }

  .donora-logo {
    margin-bottom: 15px;
  }

  .donora-hero-text p {
    max-width: 195px;
  }

  .donora-hero-art img {
    right: -100px;
    top: -30px;
    width: 330px;
  }

  .category-card {
    min-height: 56px;
    padding: 6px 3px 5px;
  }

  .category-card svg {
    width: 22px;
    height: 22px;
  }

  .category-card span {
    font-size: 8px;
  }

  .donora-primary strong {
    font-size: 15px;
  }

  .donora-primary small {
    font-size: 11.2px;
  }
}

@media (max-width: 360px) {
  .donora-content {
    width: calc(100% - 12px);
  }

  .donora-hero-card,
  .donora-actions {
    border-radius: 14px;
  }

  .donora-hero-card {
    padding: 14px 11px 10px;
  }

  .donora-hero-text p {
    max-width: 178px;
  }

  .donora-hero-art img {
    right: -76px;
    top: -18px;
    width: 284px;
    opacity: 1;
  }

  .donora-categories {
    gap: 5px;
  }

  .category-card {
    min-height: 52px;
    border-radius: 10px;
  }

  .category-card svg {
    width: 20px;
    height: 20px;
  }

  .category-card span {
    font-size: 7.4px;
  }

  .donora-actions {
    padding: 13px 12px 10px;
  }

  .action-row {
    grid-template-columns: 36px minmax(0, 1fr);
    gap: 9px;
    padding-bottom: 8px;
    margin-bottom: 8px;
  }

  .action-icon {
    width: 34px;
    height: 34px;
  }

  .action-row svg {
    width: 20px;
    height: 20px;
  }

  .donora-primary {
    width: 100%;
    min-height: 54px;
    grid-template-columns: 1fr 14px;
    padding: 7px 8px;
  }

  .button-content {
    gap: 9px;
    padding-left: 8px;
  }

  .button-heart,
  .button-heart svg {
    width: 36px;
    height: 36px;
  }
}

@media (max-width: 330px) {
  .donora-hero-art img {
    opacity: 0.96;
    right: -66px;
    top: -16px;
    width: 264px;
  }

  .donora-categories {
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 4px;
  }

  .category-card {
    min-height: 47px;
    padding: 5px 2px 4px;
  }

  .category-card svg {
    width: 17px;
    height: 17px;
  }

  .category-card span {
    font-size: 6.4px;
    line-height: 1.08;
  }

  .donora-primary {
    width: 100%;
    grid-template-columns: 1fr 12px;
  }

  .button-content {
    gap: 8px;
    padding-left: 6px;
  }

  .button-heart,
  .button-heart svg {
    width: 32px;
    height: 32px;
  }

  .donora-primary small {
    font-size: 10.6px;
  }
}
</style>
